/**
 * Home health demo store — React bindings over useSyncExternalStore.
 *
 * `useDemoSelector` caches the last selected value and only publishes a new one
 * when `isEqual` says it changed, so selectors may return fresh objects.
 *
 * **The cache is keyed on the selector as well as on the state**, and that is
 * not an optimisation detail — it is the whole correctness of the hook. A
 * selector that closes over a prop answers a different question when that prop
 * changes, and the store has no idea it happened. Keyed on state alone, the hook
 * kept handing back the answer to the *old* question until something unrelated
 * dispatched into the store.
 *
 * That is what made the patient page look haunted for three days (Pete,
 * 2026-09-20 *"it did magically appear at one point"*, 2026-09-21 *"I see extra
 * skeletons during load. When product components load all skeletons disappear.
 * No extra sections at the top"*). `HomeHealthPatientSection` selects on the
 * patient id the product's own query hands it. That id arrives a beat after the
 * first render, the demo store does not move when it does, so the selector never
 * re-ran — and the section stayed on its "no such patient" answer until the
 * 30-second horizon poll dispatched a snapshot and knocked the cache out. Hence
 * panels that appeared minutes later for no reason anybody could see.
 */
import { useCallback, useRef, useSyncExternalStore } from "react";
import type { DemoEvent } from "./events";
import { getDemoStore } from "./instance";
import type { DemoState } from "@caredna-marlin/utils/homeHealthDemo/state";

export function shallowEqual<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  const ka = Object.keys(a as object);
  const kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (!Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false;
  }
  return true;
}

export function useDemoState(): DemoState {
  const store = getDemoStore();
  return useSyncExternalStore(store.subscribe, store.getState, store.getState);
}

export function useDemoSelector<T>(selector: (state: DemoState) => T, isEqual: (a: T, b: T) => boolean = Object.is): T {
  const store = getDemoStore();
  const cache = useRef<{ state: DemoState; selector: (state: DemoState) => T; value: T } | undefined>(undefined);
  const getSnapshot = useCallback(() => {
    const state = store.getState();
    // Same state *and* the same question: the answer cannot have changed.
    if (cache.current && cache.current.state === state && cache.current.selector === selector) return cache.current.value;
    const next = selector(state);
    // A recompute is not a re-render: an equal value keeps its old identity, so
    // an inline selector costs a call and nothing else.
    if (cache.current && isEqual(cache.current.value, next)) {
      cache.current = { state, selector, value: cache.current.value };
      return cache.current.value;
    }
    cache.current = { state, selector, value: next };
    return next;
  }, [store, selector, isEqual]);
  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}

export function useDemoDispatch(): (event: DemoEvent) => void {
  const store = getDemoStore();
  return store.dispatch;
}
